import { Box, Heading, Text, VStack, HStack, Link, Badge } from "@chakra-ui/react"
import { useContext } from "react"
import { ThemeContext } from "../context/ThemeContext"
import { LanguageContext } from "../context/LanguageContext"
import LazyItem from "./LazyItem"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faExternalLinkAlt, faCalendarAlt, faMapMarkerAlt } from "@fortawesome/free-solid-svg-icons"

const Experience = () => {
    const { theme } = useContext(ThemeContext)
    const { t, language } = useContext(LanguageContext)

    const getExperiences = () => [
        {
            title: t('experience.fullStackDev'),
            company: "Anonymous PC",
            period: `2023 - ${t('experience.present')}`,
            location: t('experience.freelance'),
            type: t('experience.ecommerce'),
            url: "https://anonymouspc.net/",
            description: t('experience.anonymousPcDesc'),
            technologies: ["React", "Node.js", "Express", "PostgreSQL", "Docker", "Oracle Cloud", "Nginx"],
            achievements: language === 'es' ? [
                "Sistema completo de gestión de inventario en tiempo real",
                "Integración con múltiples pasarelas de pago",
                "Despliegue automatizado con Docker en Oracle Cloud",
                "Panel administrativo para gestión de productos y pedidos"
            ] : [
                "Complete real-time inventory management system",
                "Integration with multiple payment gateways",
                "Automated deployment with Docker on Oracle Cloud",
                "Administrative panel for product and order management"
            ]
        },
        {
            title: t('experience.erpDev'),
            company: "Anonymous PC Dashboard",
            period: `2024 - ${t('experience.present')}`, 
            location: t('experience.freelance'),
            type: t('experience.management'),
            url: "https://dashboard.anonymouspc.net/",
            description: t('experience.erpDesc'),
            technologies: ["React", "Node.js", "Express", "PostgreSQL", "Chart.js", "JWT", "Docker", "N8n"],
            achievements: language === 'es' ? [
                "Dashboard analítico con métricas de negocio en tiempo real",
                "Sistema de gestión de proveedores y compras",
                "Control de inventario con alertas automáticas",
                "Agentes inteligentes con N8n para automatización de workflows",
                "Generación de reportes financieros y operativos",
                "Sistema de autenticación y roles de usuario"
            ] : [
                "Analytical dashboard with real-time business metrics",
                "Supplier and purchasing management system",
                "Inventory control with automatic alerts",
                "Intelligent agents with N8n for workflow automation",
                "Financial and operational report generation",
                "Authentication system and user roles"
            ]
        },
        {
            title: t('experience.aiSpecialist'),
            company: t('experience.diverseProjects'),
            period: `2024 - ${t('experience.present')}`,
            location: t('experience.freelance'),
            type: t('experience.automation'),
            description: t('experience.aiDesc'),
            technologies: ["N8n", "Python", "OpenAI API", "PostgreSQL", "RAG", "Webhooks", "REST APIs"],
            achievements: language === 'es' ? [
                "Creación de agentes conversacionales con contexto empresarial",
                "Automatización de procesos de atención al cliente",
                "Integración de múltiples servicios via APIs",
                "Implementación de sistemas RAG para consulta de documentos",
                "Optimización de workflows empresariales con IA"
            ] : [
                "Creation of conversational agents with business context",
                "Automation of customer service processes",
                "Integration of multiple services via APIs",
                "Implementation of RAG systems for document consultation",
                "Optimization of business workflows with AI"
            ]
        }
    ]

    const experiences = getExperiences()

    return (
        <Box
            background={theme.bg.experiencia}
            transitionProperty="background" 
            transitionDuration="0.3s"
            id="experiencia"
            padding="5% 0"
        >
            <VStack spacing={8} maxW="1200px" mx="auto" px="5%">
                <Heading
                    as="h2"
                    fontSize="50px"
                    color={theme.color}
                    textAlign="center"
                    transitionProperty="color" 
                    transitionDuration="0.3s"
                >
                    {t('experience.title')}
                </Heading>

                {experiences.map((exp, index) => (
                    <LazyItem key={index} index={index} delay={0.1}>
                        <Box
                            w="100%"
                            p={8}
                            borderRadius="lg"
                            border="2px solid"
                            borderColor={theme.borderColor || "#e2e8f0"}
                            bg={theme.bg.card || "white"}
                            shadow="lg"
                            transition="all 0.3s ease"
                            _hover={{
                                transform: "translateY(-4px)",
                                shadow: "xl"
                            }}
                        >
                        <VStack align="start" spacing={4}>
                            {/* Header */}
                            <HStack justify="space-between" w="100%" flexWrap="wrap">
                                <VStack align="start" spacing={1}>
                                    <Heading
                                        as="h3"
                                        fontSize="1.8rem"
                                        color={theme.color}
                                        transitionProperty="color" 
                                        transitionDuration="0.3s"
                                    >
                                        {exp.title}
                                    </Heading>
                                    <HStack>
                                        <Text
                                            fontSize="1.3rem"
                                            fontWeight="bold"
                                            color="blue.500"
                                        >
                                            {exp.company}
                                        </Text>
                                        {exp.url && (
                                            <Link href={exp.url} isExternal color="blue.500">
                                                <FontAwesomeIcon icon={faExternalLinkAlt} />
                                            </Link>
                                        )}
                                    </HStack>
                                </VStack>

                                <VStack align="end" spacing={1}>
                                    <HStack>
                                        <FontAwesomeIcon icon={faCalendarAlt} color="#666" />
                                        <Text color={theme.color} fontWeight="medium">
                                            {exp.period}
                                        </Text>
                                    </HStack>
                                    <HStack>
                                        <FontAwesomeIcon icon={faMapMarkerAlt} color="#666" />
                                        <Text color={theme.color}>
                                            {exp.location}
                                        </Text>
                                    </HStack>
                                    <Badge colorScheme="green" variant="subtle">
                                        {exp.type}
                                    </Badge>
                                </VStack>
                            </HStack>

                            {/* Description */}
                            <Text
                                fontSize="1.1rem"
                                color={theme.color}
                                lineHeight="1.6"
                                transitionProperty="color" 
                                transitionDuration="0.3s"
                            >
                                {exp.description}
                            </Text>

                            {/* Technologies */}
                            <Box>
                                <Text
                                    fontSize="1.1rem"
                                    fontWeight="bold"
                                    color={theme.color}
                                    mb={2}
                                >
                                    {t('experience.technologies')}
                                </Text>
                                <HStack flexWrap="wrap" spacing={2}>
                                    {exp.technologies.map((tech, techIndex) => (
                                        <Badge
                                            key={techIndex}
                                            colorScheme="blue"
                                            variant="outline"
                                            px={3}
                                            py={1}
                                            borderRadius="full"
                                        >
                                            {tech}
                                        </Badge>
                                    ))}
                                </HStack>
                            </Box>

                            {/* Achievements */}
                            <Box>
                                <Text
                                    fontSize="1.1rem"
                                    fontWeight="bold"
                                    color={theme.color}
                                    mb={2}
                                >
                                    {t('experience.achievements')}
                                </Text>
                                <VStack align="start" spacing={1}>
                                    {exp.achievements.map((achievement, achIndex) => (
                                        <HStack key={achIndex} align="start">
                                            <Text color="green.500" fontWeight="bold">•</Text>
                                            <Text
                                                fontSize="1rem"
                                                color={theme.color}
                                                lineHeight="1.5"
                                            >
                                                {achievement}
                                            </Text>
                                        </HStack>
                                    ))}
                                </VStack>
                            </Box>
                        </VStack>
                        </Box>
                    </LazyItem>
                ))}
            </VStack>
        </Box>
    )
}

export default Experience
